import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useMyApplications } from '../../hooks/useMyApplications';
import {
  APPLICATION_STATUS_LABELS,
  POST_TYPE_LABELS,
  getPostDetailPath,
  getApplicationChatPath,
} from '../../services/api/applications.service';
import {
  FileCheck,
  MapPin,
  Briefcase,
  Calendar,
  MessageSquare,
  ChevronRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const getPreviewMessage = (application) => {
  const text = application?.chat?.messages?.[0]?.text
  if (!text) return null
  return text.replace(/📎\s*CV\s*:\s*https?:\/\/\S+/gi, '').trim() || text
}

export default function MyApplications() {
  const { applications, loading, error, refresh } = useMyApplications();

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Mes candidatures</h1>
          <p className="text-slate-600">
            Suivez l&apos;état de vos candidatures aux offres d&apos;emploi et de vos réponses aux annonces.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4" data-testid="applications-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="lux-dashboard-card p-6 animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-1/4 mb-4" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div
            className="bg-white rounded-lg border border-red-200 p-8 text-center"
            data-testid="applications-error"
          >
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Chargement impossible</h3>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={refresh} variant="outline">
              Réessayer
            </Button>
          </div>
        ) : applications.length === 0 ? (
          <div className="lux-dashboard-card p-12 text-center" data-testid="applications-empty">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileCheck className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune candidature</h3>
            <p className="text-slate-600 mb-6">
              Vous n&apos;avez pas encore postulé à une offre ou répondu à une annonce.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://clicketpro.fr/emplois">
                <Button className="btn-clickpro-primary w-full sm:w-auto">
                  Voir les offres d&apos;emploi
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Link to="/demandes">
                <Button variant="outline" className="w-full sm:w-auto">
                  Parcourir les demandes
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const status = APPLICATION_STATUS_LABELS[app.status] ?? {
                label: app.status,
                color: 'bg-slate-100 text-slate-800',
              };
              const post = app.post ?? {};
              const postPath = getPostDetailPath(post);
              const chatPath = getApplicationChatPath(app);
              const previewMessage = getPreviewMessage(app);
              const postTypeLabel = POST_TYPE_LABELS[post.type] ?? post.type ?? 'Annonce';

              return (
                <article
                  key={app.id}
                  className="lux-dashboard-card-interactive p-6"
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={status.color}>{status.label}</Badge>
                      <Badge variant="outline" className="text-slate-600">
                        {postTypeLabel}
                      </Badge>
                    </div>
                    <span className="text-sm text-slate-500 flex items-center gap-1 shrink-0">
                      <Calendar className="w-4 h-4" />
                      {new Date(app.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>

                  <h2 className="text-lg font-semibold text-slate-900 mb-2">
                    {postPath ? (
                      <Link to={postPath} className="hover:text-[hsl(var(--primary))] transition-colors">
                        {post.title || 'Annonce sans titre'}
                      </Link>
                    ) : (
                      post.title || 'Annonce sans titre'
                    )}
                  </h2>

                  {previewMessage && (
                    <p className="text-slate-600 line-clamp-3 mb-4 whitespace-pre-line">{previewMessage}</p>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      {post.author?.name && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {post.author.name}
                        </span>
                      )}
                      {post.locationCity && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {post.locationCity}
                        </span>
                      )}
                      {post.category?.name && (
                        <Badge variant="secondary" className="font-normal">
                          {post.category.name}
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {chatPath && (
                        <Link to={chatPath}>
                          <Button size="sm" variant="outline" data-testid={`application-chat-${app.id}`}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Messagerie
                          </Button>
                        </Link>
                      )}
                      {postPath && (
                        <Link to={postPath}>
                          <Button size="sm" className="btn-clickpro-primary">
                            Voir l&apos;annonce
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && applications.length > 0 && (
          <div className="mt-6 flex justify-end">
            <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualiser'}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
